import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError, api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [forgot, setForgot] = useState(false);
  const [info, setInfo] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setInfo(data.message);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-ink text-white p-14 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-500/25 blur-3xl" />
        <div className="absolute -left-20 bottom-10 h-72 w-72 rounded-full bg-brand-900/30 blur-3xl" />
        <div className="flex items-center gap-4 relative">
          <div className="h-14 w-14 rounded-md bg-white flex items-center justify-center p-1.5"><img src="/jn-logo.png" alt="JN Expertise" className="h-full w-full object-contain" /></div>
          <div>
            <div className="font-heading font-bold text-xl leading-tight">JN Expertise</div>
            <div className="text-xs text-brand-300 tracking-wider uppercase">TaxeSalaires</div>
          </div>
        </div>
        <div className="fade-up relative">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Calculez et déclarez<br />la taxe sur les salaires<br /><span className="text-brand-300">sans erreur.</span></h1>
          <p className="mt-6 text-slate-300 max-w-md text-base">Barème progressif 2019–2026, franchise, décote, abattement associations, rapport d'assujettissement, formulaires 2501-SD et 2502-SD prêts à exporter.</p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[["4,25 %", "jusqu'à 9 229 €", "#B8B0E7"], ["8,50 %", "9 229 → 18 423 €", "#F0A83A"], ["13,60 %", "au-delà", "#F06B6B"]].map(([r, l, c]) => (
              <div key={r} className="rounded-md border border-ink-border bg-ink-light/70 p-3">
                <div className="font-mono text-xl font-semibold" style={{ color: c }}>{r}</div>
                <div className="text-xs text-slate-400 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-500 relative">JN Expertise · Audit, expertise comptable & conseil · Barème 2026 · Art. 231 et 1679 A du CGI</div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md bg-card border border-border border-t-4 border-t-brand-500 rounded-md p-8 fade-up" data-testid="auth-card">
          <img src="/jn-logo.png" alt="JN Expertise" className="h-14 w-auto object-contain lg:hidden mb-4" />
          <h2 className="font-heading text-2xl font-bold text-foreground">{forgot ? "Mot de passe oublié" : "Bienvenue"}</h2>
          <p className="text-sm text-muted-foreground mt-1">{forgot ? "Indiquez votre e-mail, nous vous enverrons un lien de réinitialisation valable 1 heure." : "Connectez-vous pour accéder à vos dossiers."}</p>
          {forgot ? (
            <form onSubmit={submitForgot} className="mt-6 space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="femail">E-mail</Label>
                <Input id="femail" type="email" value={form.email} onChange={set("email")} required data-testid="forgot-email-input" />
              </div>
              {info && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/40 rounded-md px-3 py-2" data-testid="forgot-success">{info}</div>}
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/40 rounded-md px-3 py-2" data-testid="auth-error">{error}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-button">{busy ? "Envoi…" : "Envoyer le lien"}</Button>
              <button type="button" onClick={() => { setForgot(false); setInfo(""); setError(""); }} className="block w-full text-center text-sm text-muted-foreground hover:text-brand-600" data-testid="forgot-back-button">Retour à la connexion</button>
            </form>
          ) : (
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }} className="mt-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Créer un compte</TabsTrigger>
            </TabsList>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <TabsContent value="register" className="m-0">
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" value={form.name} onChange={set("name")} required={tab === "register"} data-testid="register-name-input" />
              </TabsContent>
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="login-password-input" />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/40 rounded-md px-3 py-2" data-testid="auth-error">{error}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
                {busy ? "Veuillez patienter…" : tab === "login" ? "Se connecter" : "Créer mon compte"}
              </Button>
              {tab === "login" && <button type="button" onClick={() => { setForgot(true); setError(""); }} className="block w-full text-center text-sm text-muted-foreground hover:text-brand-600" data-testid="forgot-password-link">Mot de passe oublié ?</button>}
            </form>
          </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
